/**
 * Artifact workbench contracts (public, browser-safe).
 *
 * Field shapes mirror `docs/2026-08-28-sidebar-artifact-workbench/02-contract-storage-api.md`
 * §2/§7. Pure TypeScript, zero dependencies, shared by reducer/store/viewers.
 */

/** Delivery kinds surfaced by the artifact workbench. */
export type PublicArtifactKind =
  | 'presentation'
  | 'document'
  | 'spreadsheet'
  | 'image'
  | 'text'
  | 'code'
  | 'data'
  | 'archive'
  | 'unknown'

/** Artifact lifecycle statuses (02 §2). */
export type PublicArtifactStatus =
  | 'queued'
  | 'generating'
  | 'validating'
  | 'previewing'
  | 'ready'
  | 'failed'
  | 'cancelled'
  | 'expired'

export type PublicArtifactProgressStatus = Extract<
  PublicArtifactStatus,
  'queued' | 'generating' | 'validating' | 'previewing'
>

export type PublicArtifactTerminalStatus = Extract<
  PublicArtifactStatus,
  'ready' | 'failed' | 'cancelled' | 'expired'
>

/** Minimal public projection of one immutable artifact version (02 §2). */
export interface PublicArtifactRef {
  schemaVersion: '1'
  artifactId: string
  artifactFamilyId?: string
  revision?: number
  kind: PublicArtifactKind
  format: string
  status: PublicArtifactStatus
  displayName: string
  mimeType?: string
  size?: number
  checksum?: string
  originMessageId?: string
  deliveryGroupId?: string
  supersedesArtifactId?: string
  preview: {
    strategy: 'pages' | 'image' | 'markdown' | 'text' | 'table' | 'none'
    pageCount?: number
    state?: 'pending' | 'ready' | 'failed' | 'unavailable'
    failure?: { code: string; safeMessage: string }
  }
  capabilities: Array<'preview' | 'download' | 'revise'>
  createdAt: string
  expiresAt: string
}

/**
 * Page preview manifest served by `GET .../artifacts/{id}/preview` (02 §7).
 * `defaultPage` is 1-based and must sit within [1, pageCount].
 */
export interface PagePreviewManifest {
  schemaVersion: '1'
  artifactId: string
  revision: number
  pageCount: number
  defaultPage: number
  pages: Array<{
    page: number
    width: number
    height: number
    checksum: string
    thumbnailAvailable: boolean
  }>
}

/** Cursor-page list result of `GET .../artifacts` (02 §5). */
export interface ArtifactListResult {
  refs: PublicArtifactRef[]
  nextCursor?: string
}

/** Bounded UTF-8 hydration returned by the managed artifact content route. */
export interface BoundedArtifactContent {
  schemaVersion: '1'
  artifactId: string
  revision: number
  format: string
  mimeType: string
  content: string
}

const STATUS_PHASE: Readonly<Record<PublicArtifactStatus, number>> = {
  queued: 10,
  generating: 20,
  validating: 30,
  previewing: 40,
  ready: 50,
  failed: 60,
  cancelled: 70,
  expired: 80,
}

/** True once an artifact reached a state that never regresses. */
export function isArtifactTerminal(status: PublicArtifactStatus): boolean {
  return STATUS_PHASE[status] >= STATUS_PHASE.ready
}

/** True when the manifest grants preview and the ref is usable. */
export function isArtifactPreviewable(ref: PublicArtifactRef): boolean {
  return ref.status === 'ready' && ref.capabilities.includes('preview')
}

/** A verified original may be downloaded while its preview is still building. */
export function isArtifactDownloadable(ref: PublicArtifactRef): boolean {
  return (ref.status === 'ready' || ref.status === 'previewing') && ref.capabilities.includes('download')
}

/**
 * Viewer strategy for a ref. A declared `preview.strategy` wins when it is
 * meaningful; otherwise the format whitelist decides (02 §6.1). Unknown
 * formats degrade to `none` (metadata + download only).
 */
export function resolvePreviewStrategy(
  ref: PublicArtifactRef,
): PublicArtifactRef['preview']['strategy'] {
  if (ref.preview?.state === 'pending' || ref.preview?.state === 'failed') return 'none'
  const declared = ref.preview?.strategy
  if (declared && declared !== 'none') return declared
  const format = (ref.format || '').toLowerCase()
  if (format === 'markdown' || format === 'md') return 'markdown'
  if (format === 'txt' || format === 'text' || format === 'js' || format === 'json') return 'text'
  if (format === 'png' || format === 'jpeg' || format === 'jpg' || format === 'webp') return 'image'
  if (format === 'xlsx') return 'table'
  return 'none'
}

/** Numeric revision comparison for family/revision UI (Phase 3). */
export function compareRevision(a: PublicArtifactRef, b: PublicArtifactRef): number {
  const left = typeof a.revision === 'number' ? a.revision : 0
  const right = typeof b.revision === 'number' ? b.revision : 0
  return left - right
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

const PUBLIC_KINDS = [
  'presentation',
  'document',
  'spreadsheet',
  'image',
  'text',
  'code',
  'data',
  'archive',
  'unknown',
] as const

const PUBLIC_STATUSES = [
  'queued',
  'generating',
  'validating',
  'previewing',
  'ready',
  'failed',
  'cancelled',
  'expired',
] as const

const PREVIEW_STRATEGIES = ['pages', 'image', 'markdown', 'text', 'table', 'none'] as const

const CAPABILITIES = ['preview', 'download', 'revise'] as const

/**
 * Runtime coercion for untrusted attachment payloads (stream/history/list).
 * Returns a typed ref, or null when the payload violates the contract.
 * Callers MUST skip+warn, never throw, on invalid entries (06 spec §3.3).
 */
export function coerceArtifactRef(value: unknown): PublicArtifactRef | null {
  if (!isPlainObject(value)) return null
  const id = value.artifactId
  if (typeof id !== 'string' || !id.trim()) return null
  const kind = value.kind
  if (typeof kind !== 'string' || !(PUBLIC_KINDS as readonly string[]).includes(kind)) return null
  const status = value.status
  if (typeof status !== 'string' || !(PUBLIC_STATUSES as readonly string[]).includes(status)) return null
  const displayName = typeof value.displayName === 'string' && value.displayName.trim()
    ? value.displayName.trim()
    : id
  const preview = isPlainObject(value.preview)
    ? {
        strategy: PREVIEW_STRATEGIES.includes(value.preview.strategy as never)
          ? (value.preview.strategy as PublicArtifactRef['preview']['strategy'])
          : ('none' as const),
        ...(typeof value.preview.pageCount === 'number' && Number.isFinite(value.preview.pageCount)
          ? { pageCount: Math.max(0, Math.floor(value.preview.pageCount)) }
          : {}),
      }
    : ({ strategy: 'none' } as PublicArtifactRef['preview'])
  const capabilities = Array.isArray(value.capabilities)
    ? value.capabilities.filter((item): item is PublicArtifactRef['capabilities'][number] =>
        typeof item === 'string' && (CAPABILITIES as readonly string[]).includes(item))
    : []
  if (isPlainObject(value.preview)) {
    if (['pending', 'ready', 'failed', 'unavailable'].includes(String(value.preview.state))) {
      (preview as PublicArtifactRef['preview']).state = value.preview.state as NonNullable<PublicArtifactRef['preview']['state']>
    }
    if (value.preview.state === 'failed' && isPlainObject(value.preview.failure)
      && typeof value.preview.failure.code === 'string' && typeof value.preview.failure.safeMessage === 'string') {
      (preview as PublicArtifactRef['preview']).failure = {
        code: value.preview.failure.code.slice(0, 128), safeMessage: value.preview.failure.safeMessage.slice(0, 512),
      }
    }
  }
  const ref: PublicArtifactRef = {
    schemaVersion: '1',
    artifactId: id,
    kind: kind as PublicArtifactKind,
    format: typeof value.format === 'string' && value.format ? value.format : 'unknown',
    status: status as PublicArtifactStatus,
    displayName,
    preview,
    capabilities,
    createdAt: typeof value.createdAt === 'string' ? value.createdAt : new Date(0).toISOString(),
    expiresAt: typeof value.expiresAt === 'string' ? value.expiresAt : '',
  }
  if (typeof value.artifactFamilyId === 'string') ref.artifactFamilyId = value.artifactFamilyId
  if (typeof value.revision === 'number' && Number.isFinite(value.revision)) {
    ref.revision = Math.max(0, Math.floor(value.revision))
  }
  if (typeof value.mimeType === 'string') ref.mimeType = value.mimeType
  if (typeof value.size === 'number' && Number.isFinite(value.size)) ref.size = value.size
  if (typeof value.checksum === 'string') ref.checksum = value.checksum
  if (typeof value.originMessageId === 'string' && value.originMessageId) {
    ref.originMessageId = value.originMessageId
  }
  if (typeof value.deliveryGroupId === 'string') ref.deliveryGroupId = value.deliveryGroupId
  if (typeof value.supersedesArtifactId === 'string') ref.supersedesArtifactId = value.supersedesArtifactId
  return ref
}

/** Runtime coercion for the preview manifest; null on contract violation. */
export function coercePagePreviewManifest(value: unknown): PagePreviewManifest | null {
  if (!isPlainObject(value)) return null
  const artifactId = value.artifactId
  if (typeof artifactId !== 'string' || !artifactId.trim()) return null
  const pageCount = value.pageCount
  if (typeof pageCount !== 'number' || !Number.isFinite(pageCount) || pageCount <= 0) return null
  const pages = Array.isArray(value.pages) ? value.pages : []
  const coercedPages = pages.flatMap((entry, index) => {
    if (!isPlainObject(entry)) return []
    const pageNumber = typeof entry.page === 'number' && Number.isFinite(entry.page)
      ? Math.floor(entry.page)
      : index + 1
    return [{
      page: pageNumber,
      width: typeof entry.width === 'number' ? entry.width : 0,
      height: typeof entry.height === 'number' ? entry.height : 0,
      checksum: typeof entry.checksum === 'string' ? entry.checksum : '',
      thumbnailAvailable: entry.thumbnailAvailable === true,
    }]
  })
  if (!coercedPages.length) return null
  return {
    schemaVersion: '1',
    artifactId,
    revision: typeof value.revision === 'number' ? value.revision : 1,
    pageCount,
    defaultPage: typeof value.defaultPage === 'number' && value.defaultPage >= 1
      ? Math.floor(value.defaultPage)
      : 1,
    pages: coercedPages,
  }
}

/** Extract browser-safe text from the canonical backend payload (and legacy fixtures). */
export function artifactTextContent(value: unknown): string {
  if (typeof value === 'string') return value
  if (!isPlainObject(value)) return ''
  if (typeof value.content === 'string') return value.content
  return typeof value.text === 'string' ? value.text : ''
}
